import type { FaultConfig, KpiConfig } from '../../engine/plant/types';

export const faults: FaultConfig[] = [
  {
    id: 'HEAVY-CRUDE',
    name: 'Switch to a heavier crude',
    kind: 'sourceRamp',
    target: 'SRC-CRUDE',
    field: 'res',
    params: { delta: 1.4, overS: 2700 },
    description:
      'The tank switch brings a heavier assay: 14 % more residue. Less of everything valuable ' +
      'at the same charge rate, and the tower has to work harder for the same cut points.',
  },
  {
    id: 'LIGHT-CRUDE',
    name: 'Switch to a lighter crude',
    kind: 'sourceRamp',
    target: 'SRC-CRUDE',
    field: 'res',
    params: { delta: -1.2, overS: 2700 },
    description: 'A lighter assay: more overhead, more vapour load, and the tower moves toward flood.',
  },
  { id: 'HEATER-FOUL', name: 'Heater coil fouling', kind: 'fouling', target: 'H-201', params: { perHour: 0.06 }, description: 'Coke builds inside the tubes over the shift. The skin runs hotter for the same duty.' },
  { id: 'HX103-FOUL', name: 'Preheat exchanger fouling', kind: 'fouling', target: 'HX-103', params: { perHour: 0.12 }, description: 'The residue exchanger fouls. Preheat falls and the heater has to make up the difference.' },
  { id: 'HX102-FOUL', name: 'Diesel exchanger fouling', kind: 'fouling', target: 'HX-102', params: { perHour: 0.15 }, description: 'The diesel preheat exchanger fouls.' },
  {
    id: 'COND-LOSS',
    name: 'Condenser cooling loss',
    kind: 'sourceRamp',
    target: 'SRC-CW',
    field: 'pressure',
    params: { delta: -2.6e5, overS: 900 },
    description:
      'The cooling-water header loses pressure over a quarter of an hour. Condenser duty ' +
      'goes with it and the tower pressure has nowhere to go but up.',
  },
  {
    id: 'COND-JAM',
    name: 'Condenser valve jammed shut',
    kind: 'valveStuck',
    target: 'FV-301',
    params: { position: 0.06 },
    description: 'The cooling-water valve jams almost shut with no warning at all.',
  },
  { id: 'CW-WARM', name: 'Cooling water warming', kind: 'sourceRamp', target: 'SRC-CW', field: 'temperature', params: { delta: 10, overS: 5400 }, description: 'Cooling-water supply climbs 10 K, taking the condenser margin with it.' },
  { id: 'P303-TRIP', name: 'Reflux pump trip', kind: 'pumpTrip', target: 'P-303', description: 'The reflux pump stops. Without reflux the top of the tower loses its separation within minutes.' },
  { id: 'P101-TRIP', name: 'Crude charge pump trip', kind: 'pumpTrip', target: 'P-101', description: 'Charge flow is lost with the heater still fired — the classic tube-rupture scenario.' },
  {
    id: 'FG-SWING',
    name: 'Fuel-gas pressure swing',
    kind: 'sourceRamp',
    target: 'SRC-FG',
    field: 'pressure',
    params: { delta: -1.6e5, overS: 240 },
    description: 'The fuel-gas header drops 1.6 bar over four minutes as another unit takes gas.',
  },
  { id: 'FLAME-OUT', name: 'Flame failure', kind: 'flameFailure', target: 'H-201', description: 'The burners go out. Fuel must be isolated before any relight.' },
  { id: 'FV202-STICK', name: 'Fuel valve stiction', kind: 'valveStiction', target: 'FV-202', params: { S: 4, J: 2 }, description: 'The fuel valve sticks and slips, cycling the coil outlet temperature.' },
  { id: 'TT201-DRIFT', name: 'Coil outlet thermocouple drifting', kind: 'txDrift', target: 'TT-201', params: { pctPerHour: 3, sign: -1 }, description: 'The coil outlet reads progressively low, so the controller over-fires.' },
  { id: 'PT301-FREEZE', name: 'Column pressure transmitter frozen', kind: 'txFreeze', target: 'PT-301', description: 'The tower pressure transmitter holds its last reading.' },
  { id: 'AIR-LOSS', name: 'Instrument air failure', kind: 'airLoss', target: '*', description: 'Every valve strokes to its fail position. On this unit that means fuel off and every draw shut.' },
];

export const kpis: KpiConfig = {
  spec: [
    { tag: 'AT-304.PV', hi: 438, label: 'Naphtha endpoint ≤ 438 K' },
    { tag: 'AT-306.PV', hi: 623, label: 'Diesel 95 % point ≤ 623 K' },
    { tag: 'TT-201.PV', lo: 613, hi: 633, label: 'Coil outlet 613–633 K' },
    { tag: 'PT-301.PV', lo: 1.5, hi: 2.15, label: 'Column pressure 1.5–2.15 bara' },
    { tag: 'PDT-301.PV', hi: 0.55, label: 'Column ΔP ≤ 0.55 bar' },
  ],
  throughput: { tag: 'FT-101.PV', label: 'Crude charged', units: 'm³' },
  energy: [
    { tag: 'H-201.DUTY', label: 'Heater duty', units: 'MW' },
    { tag: 'C-301.QCOND', label: 'Condenser duty', units: 'MW' },
    { tag: 'HX-103.DUTY', label: 'Residue preheat', units: 'kW' },
  ],
  chemicals: [
    { tag: 'FT-304.PV', label: 'Naphtha', units: 'm³/h' },
    { tag: 'FT-305.PV', label: 'Kerosene', units: 'm³/h' },
    { tag: 'FT-306.PV', label: 'Diesel', units: 'm³/h' },
  ],
  danger: [
    { id: 'skin', label: 'Tube skin above 723 K', cond: { op: '>', tag: 'TT-202.PV', value: 723 } },
    { id: 'flood', label: 'Column flooding', cond: { op: '>', tag: 'C-301.FLOOD', value: 0.5 } },
    { id: 'press', label: 'Column above 2.4 bara', cond: { op: '>', tag: 'PT-301.PV', value: 2.4 } },
    { id: 'o2', label: 'Heater excess oxygen below 1.4 %', cond: { op: '<', tag: 'AT-202.PV', value: 1.4 } },
  ],
  loops: ['FIC-101', 'TIC-201', 'FIC-202', 'PIC-301', 'LIC-302', 'TIC-304', 'FIC-303', 'FIC-305', 'FIC-306', 'LIC-307'],
};
